<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../sheet';
import { Button } from '../button';
import { Input } from '../input';
import { Label } from '../label';
import { Textarea } from '../textarea';
import { Checkbox } from '../checkbox';
import { crossAppActions } from '../cross-app-actions';
import { alertModal } from '../modals/AlertModal.vue';
import { toast } from '../toast';
import { facilitiesRefresh, groupsFetchAll } from '../facilities';
import { appAccessDefaultsForNewTenant } from '../../cross-app-shared/services/app-access';
import { tenantCreatePlan } from '../../cross-app-shared/services/tenants';
import type { AppID, TenantID } from '../../cross-app-shared/types/db';

const isOpen = defineModel<boolean>('isOpen', { required: true });

const emit = defineEmits<{ saved: [{ tenantID: TenantID; tenantName: string }] }>();

type GroupRow = { key: number; groupName: string; facilityNamesText: string };

let groupKeyNext = 0;
function groupRowBlank(): GroupRow {
  return { key: groupKeyNext++, groupName: '', facilityNamesText: '' };
}

function appIDsDefault(): Set<AppID> {
  const defaults = appAccessDefaultsForNewTenant.normal;
  return new Set((Object.keys(defaults) as AppID[]).filter((appID) => defaults[appID]));
}

const tenantName = ref('');
const groupRows = ref<GroupRow[]>([groupRowBlank()]);
const appIDsGranted = ref(appIDsDefault());
const apps = ref<{ appID: AppID; name: string }[]>([]);
const isSaving = ref(false);

onMounted(async () => {
  apps.value = await crossAppActions.appAccess.appsList();
});

const planned = computed(() =>
  tenantCreatePlan({
    tenantName: tenantName.value,
    groups: groupRows.value.map((g) => ({ groupName: g.groupName, facilityNames: g.facilityNamesText.split('\n') })),
    appIDs: apps.value.map((a) => a.appID).filter((appID) => appIDsGranted.value.has(appID)),
  }),
);

const facilityCountPlanned = computed(() =>
  planned.value.ok ? planned.value.plan.groups.reduce((sum, g) => sum + g.facilityNames.length, 0) : 0,
);

// These apps store their data in a per-tenant Firestore database, which has to be provisioned separately:
const appIDsNeedingFirestore: AppID[] = ['compliance', 'parsing', 'determinations'];
const appsNeedingFirestoreSelected = computed(() =>
  apps.value.filter((a) => appIDsNeedingFirestore.includes(a.appID) && appIDsGranted.value.has(a.appID)),
);

function appGrantedToggle(appID: AppID, isGranted: boolean) {
  const next = new Set(appIDsGranted.value);
  if (isGranted) {
    next.add(appID);
  } else {
    next.delete(appID);
  }
  appIDsGranted.value = next;
}

function formReset() {
  tenantName.value = '';
  groupRows.value = [groupRowBlank()];
  appIDsGranted.value = appIDsDefault();
}

async function handleCreate() {
  if (!planned.value.ok || isSaving.value) {
    return;
  }

  const { plan } = planned.value;
  isSaving.value = true;
  try {
    const { tenantID } = await crossAppActions.tenants.create(plan);
    await facilitiesRefresh();
    await groupsFetchAll();
    toast({
      title: 'Tenant created',
      description: `${plan.tenantName} is now available`,
    });
    emit('saved', { tenantID, tenantName: plan.tenantName });
    formReset();
    isOpen.value = false;
  } catch (e: any) {
    console.error(e);
    await alertModal({
      title: 'Unable to create tenant',
      message: `We were unable to create the tenant. Error was: ${e.message}`,
    });
  } finally {
    isSaving.value = false;
  }
}
</script>

<template>
  <Sheet v-model:open="isOpen">
    <SheetContent side="right" class="w-full overflow-auto sm:max-w-xl">
      <SheetHeader>
        <SheetTitle>Create Tenant</SheetTitle>
        <SheetDescription>
          Set up a new customer with its facility groups, facilities, and the apps it has access to.
        </SheetDescription>
      </SheetHeader>

      <div class="mt-6 space-y-6">
        <div class="space-y-2">
          <Label for="tenant-create-name">Tenant Name</Label>
          <Input id="tenant-create-name" v-model="tenantName" placeholder="Tenant Name" />
        </div>

        <div class="space-y-3">
          <div>
            <Label>Facility Groups</Label>
            <p class="text-muted-foreground text-sm">
              List facilities one per line. If no groups are given, a group named after the tenant is created with no
              facilities.
            </p>
          </div>

          <div v-for="(group, index) of groupRows" :key="group.key" class="space-y-2 rounded-md border p-3">
            <div class="flex items-center gap-2">
              <i class="fa-light fa-layer-group text-muted-foreground"></i>
              <Input v-model="group.groupName" :placeholder="`Group ${index + 1} Name`" />
              <Button
                variant="ghost"
                size="icon"
                title="Remove group"
                @click="groupRows = groupRows.filter((g) => g.key !== group.key)"
              >
                <i class="fa-light fa-trash"></i>
              </Button>
            </div>
            <div class="ml-6">
              <Textarea v-model="group.facilityNamesText" rows="4" placeholder="Facility names, one per line" />
            </div>
          </div>

          <Button variant="outline" size="sm" @click="groupRows = [...groupRows, groupRowBlank()]">
            <i class="fa-light fa-plus"></i>
            Add Group
          </Button>
        </div>

        <div class="space-y-3">
          <div>
            <Label>App Access</Label>
            <p class="text-muted-foreground text-sm">Checked apps are granted to the whole tenant.</p>
          </div>
          <div class="grid grid-cols-2 gap-2">
            <label v-for="app of apps" :key="app.appID" class="flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                :modelValue="appIDsGranted.has(app.appID)"
                @update:modelValue="appGrantedToggle(app.appID, $event === true)"
              />
              {{ app.name }}
            </label>
          </div>
          <div
            v-if="appsNeedingFirestoreSelected.length > 0"
            class="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-sm text-blue-900"
          >
            <i class="fa-light fa-circle-info mt-0.5"></i>
            <span>
              {{ appsNeedingFirestoreSelected.map((a) => a.name).join(', ') }}
              {{ appsNeedingFirestoreSelected.length === 1 ? 'requires' : 'require' }} additional backend setup before
              {{ appsNeedingFirestoreSelected.length === 1 ? 'it works' : 'they work' }} for this tenant: a Firestore
              database for the tenant must be created.
            </span>
          </div>
        </div>

        <div v-if="!planned.ok" class="rounded-md bg-amber-50 p-3 text-sm text-amber-900">
          <ul class="list-disc pl-4">
            <li v-for="error of planned.errors" :key="error">{{ error }}</li>
          </ul>
        </div>
        <p v-else class="text-muted-foreground text-sm">
          Will create {{ planned.plan.groups.length }} {{ planned.plan.groups.length === 1 ? 'group' : 'groups' }} and
          {{ facilityCountPlanned }} {{ facilityCountPlanned === 1 ? 'facility' : 'facilities' }}, with access to
          {{ planned.plan.appIDs.length }} {{ planned.plan.appIDs.length === 1 ? 'app' : 'apps' }}.
        </p>

        <div class="flex justify-end space-x-4">
          <Button variant="outline" @click="isOpen = false">
            <i class="fa-light fa-xmark"></i>
            Cancel
          </Button>
          <Button :disabled="!planned.ok || isSaving" @click="handleCreate">
            <i class="fa-light fa-plus"></i>
            {{ isSaving ? 'Creating…' : 'Create Tenant' }}
          </Button>
        </div>
      </div>
    </SheetContent>
  </Sheet>
</template>
